import type { ReactNode } from 'preact/compat'
import { useTranslation } from 'react-i18next'
import { TrashIcon } from '@/components/icons/TrashIcon'
import { Button } from './Button'

export interface ColumnDefinition<T> {
  label: string
  accessor: keyof T
  render?: (value: T[keyof T], row: T) => ReactNode
  className?: string
  width?: string
}

export interface TableProps<T extends { id: string }> {
  columns: ColumnDefinition<T>[]
  data: T[]
  onRowAction?: ((id: string) => void) | undefined
  headerAction?:
    | {
        label?: string
        onClick: () => void
        disabled?: boolean
        disabledReason?: string
      }
    | undefined
  emptyStateMessage?:
    | {
        title: string
        subtitle?: string
      }
    | undefined
  readonly?: boolean
  className?: string
  loading?: boolean
}

function HeaderAction({
  label,
  onClick,
  disabled,
  disabledReason,
}: NonNullable<TableProps<any>['headerAction']>) {
  return (
    <Button
      size='xs'
      onClick={onClick}
      disabled={disabled ?? false}
      title={disabled && disabledReason ? disabledReason : label}
      type='primary'
    >
      <div className='flex items-center gap-2 justify-center'>
        <svg
          className='w-3 h-3 text-white'
          viewBox='0 0 12 12'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M6 1V11'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          />
          <path
            d='M1 6H11'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          />
        </svg>
        <span className='align-middle'>{label}</span>
      </div>
    </Button>
  )
}

export function Table<T extends { id: string }>({
  columns,
  data,
  onRowAction,
  headerAction,
  emptyStateMessage,
  readonly = false,
  className = '',
  loading = false,
}: TableProps<T>) {
  const { t } = useTranslation()

  return (
    <div className={`w-full ${className} relative min-h-24`}>
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b-3 border-primary-200'>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`
                    text-left py-2 px-1 sm:px-2 
                    text-small font-semibold 
                    text-cencert-header-blue
                    style={column.width ? { width: column.width } : undefined}
                    ${column.className || ''}
                  `}
                >
                  {column.label}
                </th>
              ))}
              {!readonly && headerAction && (
                <th className='text-center py-2 px-1 sm:px-2 text-small font-semibold text-cencert-header-blue relative'>
                  <HeaderAction {...headerAction} />
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr
                key={row.id}
                className={`
                  hover:bg-gray-50 transition-colors
                  ${index % 2 === 0 ? 'bg-white' : 'bg-primary-50'}
                `}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={`
                      py-3 px-1 sm:px-2 
                      max-w-0 sm:max-w-none
                      ${column.className || ''}
                    `}
                  >
                    <span
                      className='
                        text-small sm:text-body-sm 
                        font-medium 
                        text-cencert-header-blue 
                        tracking-wide 
                        block 
                        truncate
                        text-wrap
                      '
                      title={String(row[column.accessor])}
                    >
                      {column.render
                        ? column.render(row[column.accessor], row)
                        : String(row[column.accessor])}
                    </span>
                  </td>
                ))}
                {!readonly && onRowAction && (
                  <td className='py-3 px-1 sm:px-2 text-center'>
                    <button
                      onClick={() => onRowAction(row.id)}
                      className='
                        inline-flex items-center justify-center 
                        p-0.5 text-gray-500 
                        hover:text-red-600 
                        transition-colors
                      '
                      aria-label={t('common.remove', {
                        name: columns[0]?.accessor
                          ? String(row[columns[0].accessor])
                          : 'item',
                      })}
                    >
                      {/* Assuming TrashIcon is importable in this generic context */}
                      <TrashIcon className='w-5 h-5' />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && emptyStateMessage && (
          <div className='text-center py-3 text-gray-500 bg-white'>
            <p className='text-body-sm'>{emptyStateMessage.title}</p>
            {emptyStateMessage.subtitle && (
              <p className='text-small mt-0.5'>{emptyStateMessage.subtitle}</p>
            )}
          </div>
        )}
        {loading && (
          <div className='absolute inset-0 z-50 bg-white/80 flex items-center justify-center'>
            <div className='w-16 h-16 border-4 border-cencert-border-gray border-t-cencert-blue rounded-full animate-spin'></div>
          </div>
        )}
      </div>
    </div>
  )
}
